import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer'

// ─── Paleta ──────────────────────────────────────────────────────────────────
const C = {
  indigo: '#3730a3',
  indigoLight: '#6366f1',
  indigoMid: '#c7d2fe',
  slate900: '#0f172a',
  slate700: '#334155',
  slate500: '#64748b',
  slate400: '#94a3b8',
  slate200: '#e2e8f0',
  slate100: '#f1f5f9',
  slate50: '#f8fafc',
  white: '#ffffff',
  green700: '#15803d',
  amber600: '#d97706',
  blue700: '#1d4ed8',
  red600: '#dc2626',
}

// Colores por situación
const SIT_COLOR = {
  Pendiente: C.amber600,
  'En curso': C.blue700,
  Atendido: C.green700,
}

// ─── Estilos ─────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    paddingTop: 44,
    paddingBottom: 56,
    paddingHorizontal: 44,
    backgroundColor: C.white,
    color: C.slate900,
  },

  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    paddingBottom: 18,
    borderBottomWidth: 2,
    borderBottomColor: C.indigoLight,
  },
  headerLeft: { flex: 1, paddingRight: 24 },
  headerBadge: {
    backgroundColor: C.indigo,
    color: C.white,
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    paddingVertical: 3,
    paddingHorizontal: 9,
    borderRadius: 3,
    alignSelf: 'flex-start',
    marginBottom: 6,
    letterSpacing: 1.2,
  },
  headerTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 16,
    color: C.slate900,
  },
  headerSub: { fontSize: 9, color: C.slate500, marginTop: 3 },
  headerRight: { alignItems: 'flex-end', gap: 4, minWidth: 150 },
  headerMeta: { fontSize: 8.5, color: C.slate500 },
  headerMetaBold: { fontFamily: 'Helvetica-Bold', color: C.slate700 },

  // ── KPIs ──────────────────────────────────────────────────────────────────
  kpiRow: { flexDirection: 'row', gap: 8, marginBottom: 22 },
  kpiCard: {
    flex: 1,
    backgroundColor: C.slate50,
    borderRadius: 6,
    padding: 12,
    borderLeftWidth: 3,
    borderLeftColor: C.indigoLight,
  },
  kpiLabel: {
    fontSize: 7.5,
    color: C.slate400,
    fontFamily: 'Helvetica-Bold',
    letterSpacing: 0.8,
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  kpiValue: { fontFamily: 'Helvetica-Bold', fontSize: 18, color: C.slate900 },

  // ── Secciones ───────────────────────────────────────────────────────────────
  section: { marginBottom: 20 },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: C.slate200,
  },
  sectionAccent: {
    width: 3,
    height: 12,
    backgroundColor: C.indigoLight,
    borderRadius: 2,
    marginRight: 7,
  },
  sectionTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8.5,
    color: C.indigo,
    textTransform: 'uppercase',
    letterSpacing: 1.1,
  },

  // ── Tabla genérica ───────────────────────────────────────────────────────────
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: C.slate100,
    borderRadius: 3,
    paddingVertical: 5,
    paddingHorizontal: 6,
    marginBottom: 2,
  },
  tableHeaderCell: {
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    color: C.slate500,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 5,
    paddingHorizontal: 6,
    borderBottomWidth: 1,
    borderBottomColor: C.slate100,
  },
  tableTotalRow: {
    flexDirection: 'row',
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderTopWidth: 1.5,
    borderTopColor: C.slate200,
    backgroundColor: C.slate50,
  },
  tableCell: { fontSize: 8, color: C.slate700 },
  tableCellBold: {
    fontSize: 8,
    color: C.slate900,
    fontFamily: 'Helvetica-Bold',
  },

  // Columnas: resumen por persona
  pPerson: { width: '28%' },
  pNum: { width: '12%', textAlign: 'right' },

  // Columnas: detalle
  dId: { width: '7%' },
  dTitle: { width: '31%' },
  dOrigin: { width: '15%' },
  dPerson: { width: '20%' },
  dSit: { width: '15%' },
  dDate: { width: '12%', textAlign: 'right' },

  emptyText: { fontSize: 9, color: C.slate400, fontStyle: 'italic' },

  footer: {
    position: 'absolute',
    bottom: 20,
    left: 44,
    right: 44,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: C.slate200,
    paddingTop: 7,
  },
  footerLeft: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  footerDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: C.indigoLight,
  },
  footerText: { fontSize: 7.5, color: C.slate400 },
  footerBold: {
    fontFamily: 'Helvetica-Bold',
    color: C.slate500,
    fontSize: 7.5,
  },
})

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (date) => {
  if (!date) return '—'
  return new Date(date).toLocaleDateString('es-CR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

// ─── Documento ───────────────────────────────────────────────────────────────
const ReportDocument = ({ tickets, byPerson, meta }) => {
  const total = tickets.length
  const enCurso = tickets.filter((t) => t.situacion === 'En curso').length
  const atendidos = tickets.filter((t) => t.situacion === 'Atendido').length
  const pendientes = tickets.filter((t) => t.situacion === 'Pendiente').length

  const grand = byPerson.reduce(
    (acc, p) => ({
      pendientes: acc.pendientes + p.pendientes,
      enCurso: acc.enCurso + p.enCurso,
      atendidos: acc.atendidos + p.atendidos,
      total: acc.total + p.total,
      slaMet: acc.slaMet + (p.slaMet || 0),
      slaBreached: acc.slaBreached + (p.slaBreached || 0),
    }),
    {
      pendientes: 0,
      enCurso: 0,
      atendidos: 0,
      total: 0,
      slaMet: 0,
      slaBreached: 0,
    }
  )
  const grandDecided = grand.slaMet + grand.slaBreached
  const grandCompliance = grandDecided
    ? Math.round((grand.slaMet / grandDecided) * 100)
    : null

  /** Formatea el % de cumplimiento de SLA. */
  const fmtPct = (pct) => (pct === null || pct === undefined ? '—' : `${pct}%`)

  return (
    <Document title="Reporte de Tickets por Persona">
      <Page size="A4" style={styles.page}>
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.headerBadge}>REPORTE POR PERSONA</Text>
            <Text style={styles.headerTitle}>Reporte de Tickets</Text>
            <Text style={styles.headerSub}>{meta.rangeLabel}</Text>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.headerMeta}>
              <Text style={styles.headerMetaBold}>Generado: </Text>
              {meta.generatedAt}
            </Text>
            {meta.originLabel && (
              <Text style={styles.headerMeta}>
                <Text style={styles.headerMetaBold}>Origen: </Text>
                {meta.originLabel}
              </Text>
            )}
            {meta.situationLabel && (
              <Text style={styles.headerMeta}>
                <Text style={styles.headerMetaBold}>Situación: </Text>
                {meta.situationLabel}
              </Text>
            )}
            {meta.personLabel && (
              <Text style={styles.headerMeta}>
                <Text style={styles.headerMetaBold}>Responsable: </Text>
                {meta.personLabel}
              </Text>
            )}
          </View>
        </View>

        {/* ── KPIs ────────────────────────────────────────────────────────── */}
        <View style={styles.kpiRow}>
          <View style={styles.kpiCard}>
            <Text style={styles.kpiLabel}>Total</Text>
            <Text style={styles.kpiValue}>{total}</Text>
          </View>
          <View
            style={[styles.kpiCard, { borderLeftColor: SIT_COLOR.Pendiente }]}
          >
            <Text style={styles.kpiLabel}>Pendientes</Text>
            <Text style={[styles.kpiValue, { color: SIT_COLOR.Pendiente }]}>
              {pendientes}
            </Text>
          </View>
          <View
            style={[styles.kpiCard, { borderLeftColor: SIT_COLOR['En curso'] }]}
          >
            <Text style={styles.kpiLabel}>En Curso</Text>
            <Text style={[styles.kpiValue, { color: SIT_COLOR['En curso'] }]}>
              {enCurso}
            </Text>
          </View>
          <View
            style={[styles.kpiCard, { borderLeftColor: SIT_COLOR.Atendido }]}
          >
            <Text style={styles.kpiLabel}>Atendidos</Text>
            <Text style={[styles.kpiValue, { color: SIT_COLOR.Atendido }]}>
              {atendidos}
            </Text>
          </View>
        </View>

        {/* ── Resumen por persona ─────────────────────────────────────────── */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionAccent} />
            <Text style={styles.sectionTitle}>
              Resumen por Persona ({byPerson.length})
            </Text>
          </View>

          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, styles.pPerson]}>
              Responsable
            </Text>
            <Text style={[styles.tableHeaderCell, styles.pNum]}>Pend.</Text>
            <Text style={[styles.tableHeaderCell, styles.pNum]}>En curso</Text>
            <Text style={[styles.tableHeaderCell, styles.pNum]}>Atend.</Text>
            <Text style={[styles.tableHeaderCell, styles.pNum]}>Total</Text>
            <Text style={[styles.tableHeaderCell, styles.pNum]}>Incum.</Text>
            <Text style={[styles.tableHeaderCell, styles.pNum]}>% SLA</Text>
          </View>

          {byPerson.length > 0 ? (
            <>
              {byPerson.map((p) => (
                <View key={p.name} style={styles.tableRow} wrap={false}>
                  <Text style={[styles.tableCell, styles.pPerson]}>
                    {p.name}
                  </Text>
                  <Text style={[styles.tableCell, styles.pNum]}>
                    {p.pendientes}
                  </Text>
                  <Text style={[styles.tableCell, styles.pNum]}>
                    {p.enCurso}
                  </Text>
                  <Text style={[styles.tableCell, styles.pNum]}>
                    {p.atendidos}
                  </Text>
                  <Text style={[styles.tableCellBold, styles.pNum]}>
                    {p.total}
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      styles.pNum,
                      p.slaBreached > 0 ? { color: C.red600 } : {},
                    ]}
                  >
                    {p.slaBreached}
                  </Text>
                  <Text style={[styles.tableCellBold, styles.pNum]}>
                    {fmtPct(p.slaCompliance)}
                  </Text>
                </View>
              ))}
              <View style={styles.tableTotalRow}>
                <Text style={[styles.tableCellBold, styles.pPerson]}>
                  TOTAL
                </Text>
                <Text style={[styles.tableCellBold, styles.pNum]}>
                  {grand.pendientes}
                </Text>
                <Text style={[styles.tableCellBold, styles.pNum]}>
                  {grand.enCurso}
                </Text>
                <Text style={[styles.tableCellBold, styles.pNum]}>
                  {grand.atendidos}
                </Text>
                <Text style={[styles.tableCellBold, styles.pNum]}>
                  {grand.total}
                </Text>
                <Text style={[styles.tableCellBold, styles.pNum]}>
                  {grand.slaBreached}
                </Text>
                <Text style={[styles.tableCellBold, styles.pNum]}>
                  {fmtPct(grandCompliance)}
                </Text>
              </View>
            </>
          ) : (
            <Text style={styles.emptyText}>
              Sin datos para los filtros seleccionados.
            </Text>
          )}
        </View>

        {/* ── Detalle de tickets ──────────────────────────────────────────── */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionAccent} />
            <Text style={styles.sectionTitle}>
              Detalle de Tickets ({total})
            </Text>
          </View>

          <View style={styles.tableHeader} fixed>
            <Text style={[styles.tableHeaderCell, styles.dId]}>#</Text>
            <Text style={[styles.tableHeaderCell, styles.dTitle]}>Título</Text>
            <Text style={[styles.tableHeaderCell, styles.dOrigin]}>Origen</Text>
            <Text style={[styles.tableHeaderCell, styles.dPerson]}>
              Responsable
            </Text>
            <Text style={[styles.tableHeaderCell, styles.dSit]}>Situación</Text>
            <Text style={[styles.tableHeaderCell, styles.dDate]}>Creado</Text>
          </View>

          {total > 0 ? (
            tickets.map((t) => (
              <View key={t.id} style={styles.tableRow} wrap={false}>
                <Text style={[styles.tableCell, styles.dId]}>{t.id}</Text>
                <Text style={[styles.tableCell, styles.dTitle]}>{t.title}</Text>
                <Text style={[styles.tableCell, styles.dOrigin]}>
                  {t.systemOrigin || '—'}
                </Text>
                <Text style={[styles.tableCell, styles.dPerson]}>
                  {t.responsableName}
                </Text>
                <Text
                  style={[
                    styles.tableCellBold,
                    styles.dSit,
                    { color: SIT_COLOR[t.situacion] || C.slate700 },
                  ]}
                >
                  {t.situacion}
                </Text>
                <Text style={[styles.tableCell, styles.dDate]}>
                  {formatDate(t.createdAt)}
                </Text>
              </View>
            ))
          ) : (
            <Text style={styles.emptyText}>
              No hay tickets para los filtros seleccionados.
            </Text>
          )}
        </View>

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        <View style={styles.footer} fixed>
          <View style={styles.footerLeft}>
            <View style={styles.footerDot} />
            <Text style={styles.footerBold}>
              Reporte de Tickets por Persona
            </Text>
            <Text style={styles.footerText}>· {meta.generatedAt}</Text>
          </View>
          <Text
            style={styles.footerText}
            render={({ pageNumber, totalPages }) =>
              `Página ${pageNumber} de ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  )
}

// ─── Exportar ─────────────────────────────────────────────────────────────────
/**
 * Genera y descarga el PDF de un reporte agregado de tickets por persona.
 *
 * @param {Array<Object>} tickets - Tickets enriquecidos (con `situacion` y `responsableName`).
 * @param {Object} options
 * @param {Array<Object>} options.byPerson - Resumen por persona
 *        ({ name, pendientes, enCurso, atendidos, total }).
 * @param {Object} options.meta - Metadatos (rangeLabel, generatedAt, originLabel,
 *        situationLabel, personLabel).
 */
const generateReportPDF = async (
  tickets,
  { byPerson = [], meta = {} } = {}
) => {
  const blob = await pdf(
    <ReportDocument tickets={tickets} byPerson={byPerson} meta={meta} />
  ).toBlob()

  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `reporte-tickets-persona-${new Date().toISOString().slice(0, 10)}.pdf`
  a.click()
  URL.revokeObjectURL(url)
}

export default generateReportPDF
